import { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { faClose, faUtensils } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { useError } from "../../../context/useErrorContext"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { GenericFoodPupop } from "../GenericFoodPupop"
import { useCreateGenericFoodEntity } from "../../../hooks/useCreateGenericFoodEntity"
import { useGetMeals } from "../../../hooks/Meals/useGetMeals"
import { useSaveMeals } from "../../../hooks/Meals/useSaveMeals"
import { MealEntity } from "../../../Objects/MealsAndDrinks/MealEntity"
import { useCreateMeals } from "../../../hooks/Meals/useCreateMeals"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { useRemoveMeal } from "../../../hooks/Meals/useRemoveMeal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { addtionalMemory } from "../../../Objects/Memory/Memory"
import { selectionMemory } from "../../../Objects/Memory/Memory"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import Button from "../../../components/Button/Button"
import { comboMemory } from "../../../Objects/Memory/Memory"
import { resolveMealComboAssociations } from "../../../components/Associations/associationResolvers"
import { AssociationsPopup } from "../../../components/Associations/AssociationsPopup"
import React from "react"

interface MealsPupopProps {
  meals?: MealEntity
  title: string
  onClonse: () => void
  onCopy?: () => void
}

export const MealsPupop: React.FC<MealsPupopProps> = ({ meals, title, onClonse, onCopy }) => {
  const { t } = useTranslation("meal")

  const isCopiedDraft = meals?.id === -1

  const [showAssociations, setShowAssociations] = React.useState(false)

  const canEdite = !usePermission(Permissions.CanEditeMeals)

  const { setError } = useError()

  const { handleSavesMeals } = useSaveMeals()

  const { handleGetMeals } = useGetMeals()

  const { handleSubmitForm, handleInitialValue, typeSelected, setTypeSelectChange } = useCreateMeals()

  const { handleRemove } = useRemoveMeal()

  const {
    name,
    description,
    status,
    coast,
    categoryId,
    additionalsAllowed,
    freeAdditionalsAllowed,
    allowedSelections,
    freeAddtionalsLimit,
    tags,
    imageAssetIds,
    imageUrls,
    hasStockControl,
    stockQuantity,
    hasNeedKitchen,
    hasPreparationTime,
    preparationTimeMinutes,
    isRodizioOnly,
    productOrderMode,
    reservationAllowedDays,
    reservationAllowedTimes,
    handleCategoryId,
    setAdditionalsAllowed,
    setFreeAdditionalsAllowed,
    setAllowedSelections,
    setPromotionsIds: setPromotions,
    setTags,
    setImageAssetIds,
    setImageUrls,
    setImagesChanged,
    handleNameChange,
    handleCoastChange,
    handleDescriptionChange,
    handleStatusChange,
    handleFreeAddtionalsLimitChange,
    handleHasStockControlChange,
    handleHasNeedKitchenChange,
    handleHasPreparationTimeChange,
    handlePreparationTimeMinutesChange,
    handleIsRodizioOnlyChange,
    handleProductOrderModeChange,
    setReservationAllowedDays,
    setReservationAllowedTimes,
    handleStockQuantityChange,
    handleGenericFoodEntity,
    handleInitialGenericFoodEntity,
  } = useCreateGenericFoodEntity(true)

  useEffect(() => {
    handleInitialValue(meals)

    handleInitialGenericFoodEntity(meals?.genericFoodEntity)
  }, [meals])

  const handleAddAddtional = async (id: number) => {
    const additional = addtionalMemory.findById(id)

    if (!additional) {
      return
    }

    setAdditionalsAllowed((e) => (e.some((e) => e.id === id) ? e : [...e, additional]))
  }

  const handleRemoveAdditional = async (id: number) => {
    setAdditionalsAllowed((e) => e.filter((e) => e.id !== id))
    setFreeAdditionalsAllowed((e) => e.filter((e) => e.id !== id))
  }

  const handleAddFreeAddtional = async (id: number) => {
    const additional = addtionalMemory.findById(id)

    if (!additional) {
      return
    }

    if (!additionalsAllowed.some((e) => e.id === id)) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_free_additional_must_be_allowed"))

      return
    }

    setFreeAdditionalsAllowed((e) => (e.some((e) => e.id === id) ? e : [...e, additional]))
  }

  const handleRemoveFreeAdditional = async (id: number) => {
    setFreeAdditionalsAllowed((e) => e.filter((e) => e.id !== id))
  }

  const handleTrySubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const hasInvalidReservationTime = reservationAllowedTimes.some((time) => {
      const value = time.trim()

      if (!/^\d{2}:\d{2}$/.test(value)) {
        return true
      }

      const [hours, minutes] = value.split(":").map(Number)

      return Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59
    })

    if (hasStockControl && (stockQuantity === "" || Number(stockQuantity) < 0)) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_stock_quantity_validation"))

      return
    }

    if (Number(freeAddtionalsLimit) < 0) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_free_additionals_limit_non_negative"))

      return
    }

    if (hasPreparationTime && Number(preparationTimeMinutes) <= 0) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_preparation_time_minutes_validation"))

      return
    }

    if (productOrderMode !== 0 && (reservationAllowedDays.length === 0 || reservationAllowedTimes.length === 0)) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_reservation_config_required"))

      return
    }

    if (productOrderMode !== 0 && hasInvalidReservationTime) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_reservation_times_invalid"))

      return
    }

    if (Number(coast) <= 0) {
      setError(t("general:label_invalid"), t("genericFoodEntity:label_product_value_must_be_greater_than_zero_meal"))

      return
    }

    const genericFoodEntity = handleGenericFoodEntity()

    const result = handleSubmitForm(genericFoodEntity)

    if ("errors" in result) {
      const errorMessages = result.errors.map((e) => e.message).join("\n")
      setError(t("general:label_invalid"), errorMessages)

      return
    }

    handleSavesMeals(result, () => {
      onClonse()
      handleGetMeals(() => {})
    })
  }

  const handleCopyClick = async () => {
    onCopy?.()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faUtensils}
              iconColor={THEME.colors.iconBGSecondaryColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <>
                    {meals && (
                      <div style={{ width: "100px", marginRight: "5px" }}>
                        <GenericDropDown
                          data={Object.mapEnumToStrings(EEnableStatusType, {
                            exclude: [EEnableStatusType.All],
                          })}
                          initialState={status}
                          disable={canEdite}
                          handlerFuncion={handleStatusChange}
                          descriptionKey="interactionDescriptions:pages.products.meals.mealsPupop.select.select"
                        />

                        <GetSpace right={10} />
                      </div>
                    )}

                    <PopupHelpButton
                      helpKey="pages.products.meals.mealspupop.1"
                      variant="global"
                      title={t("helpPopup:global.meal.title")}
                      description={t("helpPopup:global.meal.description")}
                      bullets={t("helpPopup:global.meal.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClonse}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.products.meals.mealsPupop.button.onClonse"
                    />
                  </>
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form onSubmit={handleTrySubmit}>
              <GenericFoodPupop
                productType="meal"
                canEdite={canEdite}
                canSelectType={true}
                categoryId={categoryId}
                typeSelected={typeSelected}
                name={name}
                coast={coast}
                hasNeedKitchen={hasNeedKitchen}
                hasPreparationTime={hasPreparationTime}
                preparationTimeMinutes={preparationTimeMinutes}
                isRodizioOnly={isRodizioOnly}
                productOrderMode={productOrderMode}
                reservationAllowedDays={reservationAllowedDays}
                reservationAllowedTimes={reservationAllowedTimes}
                description={description}
                freeAddtionals={freeAdditionalsAllowed}
                freeAddtionalsLimit={freeAddtionalsLimit}
                tags={tags}
                imageAssetIds={imageAssetIds}
                imageUrls={imageUrls}
                onImagesChange={(ids, urls) => {
                  setImageAssetIds(ids)

                  setImageUrls(urls)

                  setImagesChanged(true)
                }}
                hasStockControl={hasStockControl}
                stockQuantity={stockQuantity}
                addtionals={additionalsAllowed}
                selections={allowedSelections}
                genericFoodEntity={meals?.genericFoodEntity}
                removeAddtionals={handleRemoveAdditional}
                addAddtionals={handleAddAddtional}
                addFreeAddtionals={handleAddFreeAddtional}
                removeFreeAddtionals={handleRemoveFreeAdditional}
                addSelections={(id) => {
                  const selection = selectionMemory.findById(id)
                  if (!selection) return
                  setAllowedSelections((e) => (e.some((x) => x.id === id) ? e : [...e, selection]))
                }}
                removeSelections={(id) => setAllowedSelections((e) => e.filter((x) => x.id !== id))}
                handleCategoryId={handleCategoryId}
                handleTypeSelectChange={setTypeSelectChange}
                onRemove={async () => handleRemove(meals!, onClonse)}
                setPromotions={setPromotions}
                onCopy={handleCopyClick}
                associationButton={
                  meals && meals.id !== -1 ? (
                    <>
                      <GetSpace right={5} />
                      <Button
                        label={t("buttons:button_view_associations")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGPlusColor}
                        onClick={() => setShowAssociations(true)}
                        descriptionKey="interactionDescriptions:pages.products.meals.mealsPupop.button.viewAssociations"
                      />
                    </>
                  ) : null
                }
                handleTagsChange={setTags}
                handleCoastChange={handleCoastChange}
                handleDescriptionChange={handleDescriptionChange}
                handleFreeAddtionalsLimitChange={handleFreeAddtionalsLimitChange}
                handleHasStockControlChange={handleHasStockControlChange}
                handleHasNeedKitchenChange={handleHasNeedKitchenChange}
                handleHasPreparationTimeChange={handleHasPreparationTimeChange}
                handlePreparationTimeMinutesChange={handlePreparationTimeMinutesChange}
                handleIsRodizioOnlyChange={handleIsRodizioOnlyChange}
                handleProductOrderModeChange={handleProductOrderModeChange}
                handleReservationAllowedDaysChange={setReservationAllowedDays}
                handleReservationAllowedTimesChange={setReservationAllowedTimes}
                handleStockQuantityChange={handleStockQuantityChange}
                handleNameChange={handleNameChange}
              />
            </form>

            {showAssociations && meals && (
              <AssociationsPopup
                title={t("general:label_related_title", { name: meals.genericFoodEntity.name })}
                emptyMessage={t("general:label_related_empty")}
                onClose={() => setShowAssociations(false)}
                items={resolveMealComboAssociations(meals, comboMemory.listData)}
              />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
